/**
 * History modal for viewing and restoring section versions
 * Story 232: View history of changes and undo them
 *
 * Features:
 * - History table with timestamps and change summaries
 * - Preview panel showing reconstructed content
 * - Download button for exporting history
 * - Restore button to revert to a selected version
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSectionHistory, useHistoryValue } from '../../hooks/useSectionHistory';
import { MarkdownPreview } from './MarkdownPreview';
import type { HistoryEntry } from '../../types/history';

/** Time formatting constants - includes seconds for distinguishing rapid changes */
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

interface HistoryModalProps {
  /** Section internal ID (database ID) */
  sectionId: string;
  /** Section title for display */
  sectionTitle: string;
  /** Current section body for comparison */
  currentBody: string;
  /** Called when modal should close */
  onClose: () => void;
  /** Called when user restores to a version - receives the restored body content */
  onRestore?: (body: string) => void;
}

export function HistoryModal({
  sectionId,
  sectionTitle,
  currentBody,
  onClose,
  onRestore,
}: HistoryModalProps) {
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch history entries for this section
  const { data: historyData, isLoading: historyLoading, error: historyError } = useSectionHistory(sectionId);

  // Fetch reconstructed value when an entry is selected
  const { data: valueData, isLoading: valueLoading } = useHistoryValue(selectedEntryId);

  // Format timestamp for display
  const formatTimestamp = useCallback((isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, DATE_FORMAT_OPTIONS);
    } catch {
      return isoString;
    }
  }, []);

  // Get relative time using Intl.RelativeTimeFormat
  const getRelativeTime = useCallback((isoString: string) => {
    try {
      const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      // Use appropriate unit based on magnitude
      if (diffSecs < 60) return rtf.format(-diffSecs, 'seconds');
      if (diffMins < 60) return rtf.format(-diffMins, 'minutes');
      if (diffHours < 24) return rtf.format(-diffHours, 'hours');
      if (diffDays < 7) return rtf.format(-diffDays, 'days');
      return formatTimestamp(isoString);
    } catch {
      return formatTimestamp(isoString);
    }
  }, [formatTimestamp]);

  // Handle entry selection
  const handleSelectEntry = useCallback((entry: HistoryEntry) => {
    setSelectedEntryId(entry.id);
  }, []);

  // Handle restore action
  const handleRestore = useCallback(() => {
    if (valueData?.value && onRestore) {
      onRestore(valueData.value);
      onClose();
    }
  }, [valueData, onRestore, onClose]);

  // Handle download/export
  const handleDownload = useCallback(() => {
    if (!historyData?.entries) return;

    const exportData = {
      section_title: sectionTitle,
      section_id: sectionId,
      exported_at: new Date().toISOString(),
      entries: historyData.entries.map((entry) => ({
        id: entry.id,
        property: entry.property,
        change_type: entry.change_type,
        timestamp: entry.timestamp,
        summary: entry.summary,
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history-${sectionId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [historyData, sectionId, sectionTitle]);

  // Selected entry for highlighting
  const selectedEntry = useMemo(() => {
    if (!selectedEntryId || !historyData?.entries) return null;
    return historyData.entries.find((e) => e.id === selectedEntryId) ?? null;
  }, [selectedEntryId, historyData]);

  // Preview content - either reconstructed value or current body
  const previewContent = useMemo(() => {
    if (valueLoading) return 'Loading preview...';
    if (valueData?.value !== undefined) return valueData.value;
    return currentBody;
  }, [valueData, valueLoading, currentBody]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--z-modal-backdrop, 200)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-base, #fff)',
          borderRadius: 'var(--radius-lg, 8px)',
          width: '90%',
          maxWidth: '900px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--text-primary, #1e293b)',
              }}
            >
              History: {sectionTitle}
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: '13px',
                color: 'var(--text-muted, #94a3b8)',
              }}
            >
              View and restore previous versions
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              fontSize: '18px',
              color: 'var(--text-muted, #94a3b8)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
              lineHeight: 1,
            }}
            title="Close"
            aria-label="Close history modal"
          >
            &times;
          </button>
        </div>

        {/* Content: Split pane with history list and preview */}
        <div
          style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* History list - left side */}
          <div
            style={{
              width: '40%',
              borderRight: '1px solid var(--border-light, #e2e8f0)',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {historyLoading ? (
              <div
                style={{
                  padding: '24px',
                  color: 'var(--text-muted, #94a3b8)',
                  textAlign: 'center',
                }}
              >
                Loading history...
              </div>
            ) : historyError ? (
              <div
                style={{
                  padding: '24px',
                  color: 'var(--status-error, #ef4444)',
                  textAlign: 'center',
                }}
              >
                Failed to load history
              </div>
            ) : !historyData?.entries?.length ? (
              <div
                style={{
                  padding: '24px',
                  color: 'var(--text-muted, #94a3b8)',
                  textAlign: 'center',
                }}
              >
                No history available yet.
                <br />
                Changes will appear here after edits are saved.
              </div>
            ) : (
              <div style={{ flex: 1, overflow: 'auto' }}>
                {historyData.entries.map((entry) => (
                  <div
                    key={entry.id}
                    onClick={() => handleSelectEntry(entry)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border-light, #e2e8f0)',
                      cursor: 'pointer',
                      backgroundColor:
                        selectedEntry?.id === entry.id
                          ? 'var(--bg-muted, #f1f5f9)'
                          : 'transparent',
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: '8px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 500,
                          color: 'var(--text-primary, #1e293b)',
                        }}
                      >
                        {entry.property}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: 'var(--text-muted, #94a3b8)',
                          whiteSpace: 'nowrap',
                        }}
                        title={formatTimestamp(entry.timestamp)}
                      >
                        {getRelativeTime(entry.timestamp)}
                      </span>
                    </div>
                    {entry.summary && (
                      <p
                        style={{
                          margin: '4px 0 0 0',
                          fontSize: '12px',
                          color: 'var(--text-secondary, #64748b)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entry.summary}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview panel - right side */}
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '16px',
              backgroundColor: 'var(--bg-subtle, #f8fafc)',
            }}
          >
            {selectedEntry ? (
              <div>
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'var(--bg-muted, #f1f5f9)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: 'var(--text-secondary, #64748b)',
                  }}
                >
                  Previewing version from {formatTimestamp(selectedEntry.timestamp)}
                </div>
                <div
                  style={{
                    backgroundColor: 'var(--bg-base, #fff)',
                    borderRadius: '6px',
                    padding: '16px',
                    border: '1px solid var(--border-light, #e2e8f0)',
                  }}
                >
                  <MarkdownPreview content={previewContent} className="prose" />
                </div>
              </div>
            ) : (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted, #94a3b8)',
                  fontSize: '14px',
                }}
              >
                Select a history entry to preview
              </div>
            )}
          </div>
        </div>

        {/* Footer with actions */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <button
            onClick={handleDownload}
            disabled={!historyData?.entries?.length}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              color: 'var(--text-secondary, #64748b)',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-default, #e2e8f0)',
              borderRadius: '6px',
              cursor: historyData?.entries?.length ? 'pointer' : 'not-allowed',
              opacity: historyData?.entries?.length ? 1 : 0.5,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
            title="Download history as JSON"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                color: 'var(--text-secondary, #64748b)',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-default, #e2e8f0)',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleRestore}
              disabled={!selectedEntry || !valueData?.value || !onRestore}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                color: 'var(--text-on-brand, #fff)',
                backgroundColor: selectedEntry && valueData?.value && onRestore
                  ? 'var(--interactive, #4f46e5)'
                  : 'var(--text-muted, #94a3b8)',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedEntry && valueData?.value && onRestore
                  ? 'pointer'
                  : 'not-allowed',
                fontWeight: 500,
              }}
              title={
                !selectedEntry
                  ? 'Select a version to restore'
                  : !onRestore
                    ? 'Restore not available'
                    : 'Restore this version'
              }
            >
              Restore
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
